import { httpsCallable } from 'firebase/functions';
import { FirebaseError } from 'firebase/app';
import { functions } from '../constants/firebaseModular';
import * as Sentry from '@sentry/browser';
import type { ValidateEmailPayload, ValidateEmailResponse } from '@usacsmideo/types';

/**
 * Validates email and determines routing endpoint (login vs enroll).
 * Updated Feb 2026 for new response pattern (no status envelope).
 */
export const handleValidateEmail = async (
  payload: { email: string },
): Promise<{ data: { endpoint: 'login' | 'enroll' } }> => {
  const validateEmailFunc = httpsCallable<ValidateEmailPayload, ValidateEmailResponse>(
    functions,
    'validateEmail',
  );

  try {
    const response = await validateEmailFunc(payload.email);

    // Log non-enrolled emails so concierges can identify people who attempted
    // to use an email that isn't in our bulk-enrolled list.
    if (!response.data.userFound) {
      Sentry.captureMessage('validateEmail: non-enrolled email', {
        level: 'info',
        tags: { filtered_reason: 'non_enrolled_email' },
        extra: { email: payload.email },
      });
    }

    return { data: { endpoint: response.data.endpoint } };
  } catch (error: unknown) {
    if (error instanceof FirebaseError) {
      if (error.code === 'functions/invalid-argument') {
        // Expected: user entered a malformed or unrecognized email.
        // Downgraded to 'info' (not dropped) so concierges can identify
        // people attempting to use emails not in our bulk-enrolled list.
        Sentry.captureMessage(`validateEmail: invalid email attempt`, {
          level: 'info',
          tags: { filtered_reason: 'invalid_email_attempt' },
          extra: { email: payload.email },
        });
      } else {
        // Unexpected Firebase error — capture as a real error
        Sentry.captureException(error);
      }
      console.error('Error calling cloud function', error.code, error.message);
    } else {
      // Non-Firebase error (network, runtime, etc.)
      Sentry.captureException(error);
      console.error('Error calling cloud function', error);
    }

    // Default to login on error (safe fallback)
    return { data: { endpoint: 'login' as const } };
  }
};
